import { assert_equal, assert_instanceof, assert_not_null } from '@shared/asserts/utils_asserts';
import { DTO__base } from '@shared/common/DTO__base';
import { MAP__serializable } from '@shared/dto/_base/MAP__serializable';
import { MAP__fields_by_id } from '@shared/types/types';
import { ENUM__field_kind } from '../../_enums/ENUM__field_kind';
import { ENUM__field_graph_interaction } from '../../_fields/_base/_enums/ENUM__field_graph_interaction';
import { FN_FIELD__get_fields_map_with_graph_interaction } from '../../_fields/_base/_methods/FN_FIELD__get_fields_map_with_graph_interaction';
import { DTO_META__field_info } from '../../_fields/_base/DTO_META__field_info';
import { FIELD__base } from '../../_fields/_base/FIELD__base';
import { FIELD__list_base_generic } from '../../_fields/_primitives/list/FIELD__list_base_generic';
import { DTO_GRAPH__instance } from '../../_graph/DTO_GRAPH__instance';
import { FN_FIELD__get_by_name } from './_methods/FN_FIELD__get_by_name';
import { INVOCATION__base } from './INVOCATION__base';

declare module './INVOCATION__base' {
  interface INVOCATION__base {
    field__get(field_id: string): FIELD__base;

    fields__get_by_type<T extends FIELD__base>(target_type: new (...args: any[]) => T): T[];
    /*
     * EVENTS
     */
    field__on_changed_after(graph: DTO_GRAPH__instance, field: FIELD__base): void;

    /*
     *
     */
    fields__get_visible_in_graph_node(kinds: ENUM__field_kind[]): MAP__fields_by_id;

    fields__get_visible_in_graph_node_inputs(): MAP__fields_by_id;

    fields__get_visible_in_graph_node_outputs(): MAP__fields_by_id;
    /*
     * GRAPH VISIBLE
     */

    fields__get_visible_on_graph_inputs(): MAP__fields_by_id;

    fields__get_visible_on_graph_outputs(): MAP__fields_by_id;
    /*
     * GRAPH CONNECTABLE
     */

    fields__get_connectable_inputs(): MAP__fields_by_id;

    fields__get_connectable_outputs(): MAP__fields_by_id;

    fields__get_connectable(with_children: boolean): MAP__fields_by_id;

    fields__get_with_graph_interaction(interaction: ENUM__field_graph_interaction[], with_children: boolean): MAP__fields_by_id;

    fields__get_with_ui_component(): FIELD__base[];

    field__get_by_path(field_meta: DTO_META__field_info, field_id_could_be_different?: boolean): FIELD__base;

    field__get_parent_by_path(field_meta: DTO_META__field_info): DTO__base;
    /*
     * PRIMITIVE
     */
    fields__get_map(with_children: boolean): MAP__fields_by_id;

    fields__get_inputs(): MAP__fields_by_id;

    /*
     *
     */

    field_list__clear_without_edges<T_item extends FIELD__base>(graph: DTO_GRAPH__instance, field_list: FIELD__list_base_generic<T_item>): void;

    fields__clear_roles(): void;
  }
}

export function INIT_EXTENSION_INVOCATION__base__fields() {
  // do nothing
}

INVOCATION__base.prototype.field__get = function (field_id: string): FIELD__base {
  let fields_map = this.get_fields_map(true);

  return fields_map.get_or_assert(field_id);
};

INVOCATION__base.prototype.fields__get_by_type = function <T extends FIELD__base>(target_type: new (...args: any[]) => T): T[] {
  let out: T[] = [];

  for (const [_, field] of this.get_fields_map(true).entries()) {
    if (field instanceof target_type) out.push(field as T);
  }

  return out;
};
/*
 * EVENTS
 */

INVOCATION__base.prototype.field__on_changed_after = function (graph: DTO_GRAPH__instance, field: FIELD__base): void {
  this.calculate_node_graph_size();
};

/*
 *
 */
INVOCATION__base.prototype.fields__get_visible_in_graph_node = function (kinds: ENUM__field_kind[]) {
  let fields_map = this.get_fields_map(false);

  let target_interaction = this.meta.show_hidden_fields
    ? Object.values(ENUM__field_graph_interaction)
    : [ENUM__field_graph_interaction.VISIBLE_CONNECTABLE, ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE];

  let out: MAP__fields_by_id = new MAP__serializable();

  for (const [field_id, field] of fields_map.entries()) {
    if (!kinds.includes(field.meta.field_kind)) continue;

    let field_with_children_map = FN_FIELD__get_fields_map_with_graph_interaction(field, target_interaction, true);

    out.merge_with(field_with_children_map);
  }

  return out;
};
INVOCATION__base.prototype.fields__get_visible_in_graph_node_inputs = function () {
  return this.fields__get_visible_in_graph_node([ENUM__field_kind.INPUT]);
};

INVOCATION__base.prototype.fields__get_visible_in_graph_node_outputs = function () {
  return this.fields__get_visible_in_graph_node([ENUM__field_kind.OUTPUT]);
};

/*
 * GRAPH VISIBLE
 */
INVOCATION__base.prototype.fields__get_visible_on_graph_inputs = function (): MAP__fields_by_id {
  let target_intercation = [ENUM__field_graph_interaction.VISIBLE_CONNECTABLE, ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE];

  let out = this.fields__get_with_graph_interaction(target_intercation, true);

  for (const field of out.values()) if (field.meta.field_kind != ENUM__field_kind.INPUT) out.delete(field.meta.field_id);

  return out;
};

INVOCATION__base.prototype.fields__get_visible_on_graph_outputs = function (): MAP__fields_by_id {
  let target_intercation = [ENUM__field_graph_interaction.VISIBLE_CONNECTABLE, ENUM__field_graph_interaction.VISIBLE_NON_CONNECTABLE];

  let out = this.fields__get_with_graph_interaction(target_intercation, true);

  for (const field of out.values()) if (field.meta.field_kind != ENUM__field_kind.OUTPUT) out.delete(field.meta.field_id);

  return out;
};

/*
 * GRAPH CONNECTABLE
 */
INVOCATION__base.prototype.fields__get_connectable_inputs = function (): MAP__fields_by_id {
  let out = this.fields__get_connectable(true);

  for (const field of out.values()) if (field.meta.field_kind != ENUM__field_kind.INPUT) out.delete(field.meta.field_id);

  return out;
};

INVOCATION__base.prototype.fields__get_connectable_outputs = function (): MAP__fields_by_id {
  let out = this.fields__get_connectable(true);

  for (const field of out.values()) if (field.meta.field_kind != ENUM__field_kind.OUTPUT) out.delete(field.meta.field_id);

  return out;
};

INVOCATION__base.prototype.fields__get_connectable = function (with_children: boolean): MAP__fields_by_id {
  return this.fields__get_with_graph_interaction([ENUM__field_graph_interaction.VISIBLE_CONNECTABLE], with_children);
};

INVOCATION__base.prototype.fields__get_with_graph_interaction = function (
  interaction: ENUM__field_graph_interaction[],
  with_children: boolean,
): MAP__fields_by_id {
  let out: MAP__fields_by_id = new MAP__serializable();

  for (const [_, field] of Object.entries(this)) {
    if (!(field instanceof FIELD__base)) continue;

    out.merge_with(FN_FIELD__get_fields_map_with_graph_interaction(field, interaction, with_children));
  }

  return out;
};

INVOCATION__base.prototype.fields__get_with_ui_component = function (): FIELD__base[] {
  let out_map = this.fields__get_map(true);

  let out = out_map.values().filter(field => field.meta.ui__has_component);

  return out;
};

/*
 * PRIMITIVE
 */
INVOCATION__base.prototype.fields__get_map = function (with_children: boolean): MAP__fields_by_id {
  let out: MAP__fields_by_id = new MAP__serializable();

  for (const [_, field] of Object.entries(this)) {
    if (!(field instanceof FIELD__base)) continue;

    out.set(field.meta.field_id, field);

    if (!with_children) continue;

    let field_children = field.fields__get_map(true);

    out.merge_with(field_children);
  }

  return out;
};

INVOCATION__base.prototype.fields__get_inputs = function (): MAP__fields_by_id {
  let out: MAP__fields_by_id = new MAP__serializable();

  let fields_map = this.fields__get_map(true);

  for (const [field_id, field] of fields_map.entries()) {
    if (field.meta.field_kind != ENUM__field_kind.INPUT) continue;

    out.set(field_id, field);
  }

  return out;
};

INVOCATION__base.prototype.field__get_by_path = function (field_meta: DTO_META__field_info, field_id_could_be_different = false) {
  assert_equal(field_meta.graph_id, this.meta.graph_id);

  const path_parts = field_meta.field_path.split('.');
  const last_part = path_parts[path_parts.length - 1];

  let target_current: any = this;

  // Traverse the path to find the ultimate parent container
  for (let i = 0; i < path_parts.length - 1; i++) {
    const field_name = path_parts[i];
    // We only care about the child found to continue the descent
    target_current = FN_FIELD__get_by_name(target_current, field_name);
    assert_not_null(target_current);
  }

  // Final retrieval
  const out = FN_FIELD__get_by_name(target_current, last_part);

  if (!field_id_could_be_different) assert_equal(out.meta.field_id, field_meta.field_id);

  return out;
};

INVOCATION__base.prototype.field__get_parent_by_path = function (field_meta: DTO_META__field_info) {
  assert_equal(field_meta.graph_id, this.meta.graph_id);

  const path_parts = field_meta.field_path.split('.');
  const last_part = path_parts[path_parts.length - 1];

  let target_current: any = this;

  // Traverse the path to find the ultimate parent container
  for (let i = 0; i < path_parts.length - 1; i++) {
    const field_name = path_parts[i];
    // We only care about the child found to continue the descent
    target_current = FN_FIELD__get_by_name(target_current, field_name);
    assert_not_null(target_current);
  }

  // Final retrieval
  const out = get_field_parent_by_name(target_current, last_part);

  return out;
};

function get_field_parent_by_name(parent_object: DTO__base, field_name: string): DTO__base {
  let out: any;

  if (field_name.includes(':')) {
    let [list_name, _] = field_name.split_by_first_entry(':');
    out = (parent_object as any)[list_name];

    assert_not_null(out);
    assert_instanceof(out, FIELD__list_base_generic);
  } else {
    out = parent_object;
  }

  return out;
}

INVOCATION__base.prototype.field_list__clear_without_edges = function <T_item extends FIELD__base>(
  graph: DTO_GRAPH__instance,
  field_list: FIELD__list_base_generic<T_item>,
): void {
  if (field_list.meta.data__is_linked) return;

  let fields_to_delete = [];

  for (let item of field_list.items) {
    let edges = graph.edges__get_to([item]);
    if (!edges.is_empty()) continue;

    fields_to_delete.push(item);
  }

  if (fields_to_delete.is_empty()) return;

  for (let field_to_delete of fields_to_delete) {
    field_list.item_remove(field_to_delete);
  }

  graph.field__replace(field_list);
};

INVOCATION__base.prototype.fields__clear_roles = function () {
  let fields_map = this.get_fields_map(true);

  for (let [field_name, field] of fields_map.entries()) {
    field.meta.field_role = '';
  }
};
